/*
 * ss-key-features.js — behavior for the PDP key-features module.
 *
 * <ss-key-features>: mobile swipe carousel (photo cards); keeps the "n/N"
 * counter and the progress-track fill in sync with the scroll-snapped card
 * index.
 *
 * <ss-key-features-text>: the no-image variant's mobile carousel (Figma
 * 986:502). Text panels snap-scroll; the icon rail above ghosts inactive
 * lockups at 30%, tracks the swipe 1:1 (proportional scroll mapping), is
 * itself flickable, and its lockups double as jump buttons.
 *
 * (The buy-column strip is chips + <modal-dialog> popups now — global.js
 * machinery, no code here.)
 */

if (!customElements.get('ss-key-features')) {
  customElements.define(
    'ss-key-features',
    class SSKeyFeatures extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.skf__track');
        this.counter = this.querySelector('.skf__counter');
        this.fill = this.querySelector('.skf__progress-fill');
        this.cards = this.querySelectorAll('.skf__card');
        if (!this.track || !this.cards.length) return;
        this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      }

      onScroll() {
        window.requestAnimationFrame(() => {
          const max = this.track.scrollWidth - this.track.clientWidth;
          if (max <= 0) return;
          const progress = this.track.scrollLeft / max;
          const index = Math.round(progress * (this.cards.length - 1));
          if (this.counter) this.counter.textContent = index + 1 + '/' + this.cards.length;
          if (this.fill) {
            const segment = 100 / this.cards.length;
            this.fill.style.width = segment + '%';
            this.fill.style.transform = 'translateX(' + index * 100 + '%)';
          }
        });
      }
    }
  );
}

if (!customElements.get('ss-key-features-text')) {
  customElements.define(
    'ss-key-features-text',
    class SSKeyFeaturesText extends HTMLElement {
      connectedCallback() {
        // re-connections (variant swaps can move the element) must not
        // double-bind listeners
        if (this.__skftInit) return;
        this.__skftInit = true;
        this.track = this.querySelector('.skft__track');
        this.viewport = this.querySelector('.skft__icons-viewport');
        this.icons = Array.from(this.querySelectorAll('.skft__icon'));
        this.slides = Array.from(this.querySelectorAll('.skft__slide'));
        if (!this.track || !this.slides.length) return;

        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        // never reposition the rail while a finger is on it
        this.railTouched = false;
        if (this.viewport) {
          this.viewport.addEventListener('touchstart', () => (this.railTouched = true), { passive: true });
          this.viewport.addEventListener('touchend', () => (this.railTouched = false), { passive: true });
          this.viewport.addEventListener('touchcancel', () => (this.railTouched = false), { passive: true });
        }

        this.icons.forEach((icon, index) => {
          icon.addEventListener('click', () => {
            const slide = this.slides[index];
            if (!slide) return;
            this.track.scrollTo({
              left: slide.offsetLeft - this.slides[0].offsetLeft,
              behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
            });
          });
        });

        this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      }

      onScroll() {
        if (this.__skftRaf) return;
        this.__skftRaf = window.requestAnimationFrame(() => {
          this.__skftRaf = null;
          const x = this.track.scrollLeft;
          let active = 0;
          let bestDistance = Infinity;
          this.slides.forEach((slide, index) => {
            const distance = Math.abs(slide.offsetLeft - this.slides[0].offsetLeft - x);
            if (distance < bestDistance) {
              bestDistance = distance;
              active = index;
            }
          });
          this.setActive(active);
        });
      }

      setActive(index) {
        if (this.activeIndex === index) return;
        this.activeIndex = index;
        // instant feedback: the highlight flips as soon as the swipe commits
        this.icons.forEach((icon, i) => {
          icon.classList.toggle('skft__icon--active', i === index);
        });
        // gallery-style housekeeping (the PDP-thumbnail pattern): one short
        // native glide bringing the active lockup to its resting spot at the
        // gutter — the rail never finger-tracks the panels
        const icon = this.icons[index];
        if (!this.viewport || !icon || this.railTouched) return;
        this.viewport.scrollTo({
          left: icon.offsetLeft - this.icons[0].offsetLeft,
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        });
      }
    }
  );
}
